/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import { LAYER_STAGE, LAYER_TAB_CATEGORIES, LAYER_TABS } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { platformKeys } from "../../../../actions/Tenants/config/layerFormFields";
import { useFields } from "../../../../hooks";
import { isOfflineChecksAllowedForTenant } from "../../../../offline/allowedTenants";
import { getFilledArrayOrDefault } from "../../../../utils";
import MetaForm from "../../../Forms/MetaForm";
import { IS_SET_FILTER_OPTIONS } from "../../../Helper/LayerSortableHeader";
import Field from "../../Layers/fields/Field";
import { DisplayFilterForm } from "./DisplayFilterForm";
import FieldConfigForm from "./FieldConfigForm";
import FormConfigForm from "./FormConfigForm";
import OptionsConfigForm from "./OptionsConfigForm";
import ViewStrategyConfigForm from "./ViewStrategyConfigForm";
import { yesOrNoRadio } from "./util";

export const defaultStage = {
    text: "Intake",
    value: "intake",
    meta_display: []
};

export default function OverviewTabConfigForm({ config_data, setConfigData }) {
    const config = useConfig();
    const { applyDisplayFilterLayerFields } = useFields();
    // Hide the "Offline cache" toggle on tabs for tenants not on the
    // offline-checks allowlist (Bakker + playwright-test). The field is
    // meaningless when the master flag is unavailable. See allowedTenants.ts.
    const showOfflineCacheField = isOfflineChecksAllowedForTenant(config.tenant_id);

    const sortableOptions = useMemo(() => {
        const configuredLayerFields = applyDisplayFilterLayerFields(getFilledArrayOrDefault(config_data.layer_overview_fields));
        return [
            { value: "check.created,DESC", label: "Default [check.created,DESC]" },
            ...configuredLayerFields.filter((i: Field) => i.is_sortable())
                .flatMap((i: Field) => ["ASC", "DESC"].map((direction: string) => {
                    const value = `${i.get_backend_fieldname()[0]},${direction}`;
                    const label = `${i.label} [${value}]`;
                    return { label, value };
                }))
        ];
    }, [config_data.layer_overview_fields]);

    const form = [
        {
            type: "header",
            label: "General configuration",
        },
        {
            label: "Value",
            name: "value",
            description: "What is the slug or identifier of this tab. Used in the database and in urls.",
            type: "single-select",
            options: Object.keys(LAYER_TABS).map((i) => ({ value: LAYER_TABS[i], label: i }))
        },
        {
            label: "Value",
            name: "value",
            description: "What is the slug or identifier of this tab. Use this to use a custom slug for this tab.",
            type: "text",
        },
        {
            label: "Label",
            description: "What is the label of this tab. Used in the tab.",
            name: "text",
            type: "text"
        },
        {
            label: "Tab categories",
            name: "tab_categories",
            description: "What is the categories  does this tab belong to. Used in the url and to distinct between stock, planning en work modus. Defaults to [operation].",
            type: "single-select",
            multi: true,
            return_array: true,
            options: Object.keys(LAYER_TAB_CATEGORIES).map((i) => ({ value: LAYER_TAB_CATEGORIES[i], label: i }))
        },
        {
            label: "Display for teams",
            description: "Only show this tab for these teams.",
            multi: true,
            return_array: true,
            type: "select-managed",
            options_name: "teams",
            name: "display_for_teams",
        },
        {
            label: "Hide for teams",
            description: "Hide this tab for these teams. Can not be used together with display_for_teams.",
            multi: true,
            return_array: true,
            type: "select-managed",
            options_name: "teams",
            name: "hide_for_teams",
        },
        {
            label: "Display filter (facilities)",
            type: "element",
            name: "display_filter",
            el: <DisplayFilterForm
                object={config_data.display_filter || {}}
                setValue={(field, value) => setConfigData({ ...config_data, display_filter: { ...config_data.display_filter, [field]: value } })}
                attributes={["facilities", "exclude_facilities"]}
                withHeader={false} />
        },
        {
            label: "Layer type",
            description: "Default layer type to display in tab.",
            name: "layer_type",
            type: "single-select",
            options: [{ value: null, label: "All" }, ...config.layer_types.map((i) => ({ value: i.value, label: i.text }))]
        },
        {
            label: "Filter teams",
            description: "Only show layers assigned to these teams in this tab.",
            multi: true,
            return_array: true,
            type: "select-managed",
            options_name: "teams",
            name: "filter_teams",
        },
        {
            label: "Filter stages",
            description: "Only show layers in this stage in this tab.",
            multi: true,
            return_array: true,
            options: Object.keys(LAYER_STAGE).map((i) => ({ value: LAYER_STAGE[i], label: i })),
            name: "filter_stages",
            type: "single-select",
        },
        {
            label: "Filter check locations",
            description: "Only show layers with checks at these locations in this tab.",
            multi: true,
            return_array: true,
            options: config.check_locations.map(({ value, text }) => ({ value, label: text })),
            name: "filter_check_locations",
            type: "single-select",
        },
        {
            label: "Filter facilities",
            description: "Only show layers from these facilities in this tab.",
            multi: true,
            return_array: true,
            type: "select-managed",
            options_name: "facility",
            name: "filter_facilities",
        },
        {
            label: "Facility filter layer meta fields",
            description: "Layer meta fields to match against selected global facilities. Matches current facility OR any configured field.",
            type: "single-select",
            multi: true,
            options: platformKeys.map((key) => ({
                value: key,
                label: key,
            })),
            name: "facility_filter_meta_fields",
        },
        {
            label: "Filter layer meta",
            name: "filter_layer_meta",
            description: "Static meta filters applied to this tab. Each filter matches layers by a layer_meta field value (or a parent's). Use method 'in' with an array of values.",
            type: "object-list",
            fields: [
                {
                    label: "Source",
                    name: "source",
                    type: "single-select",
                    description: "layer_meta (default): the layer's own meta. parent_meta: meta of any active parent (methods in / not_in). parent_layer: type of the active parents, not meta (field 'type'; in: has a parent of these types, not_in: has none, e.g. not_in purchase_order,sales_order for inventory pallets).",
                    options: [
                        { value: "layer_meta", label: "layer_meta" },
                        { value: "parent_meta", label: "parent_meta" },
                        { value: "parent_layer", label: "parent_layer (parent type)" },
                    ],
                },
                {
                    label: "Field",
                    name: "field",
                    type: "text",
                    description: "meta field name, e.g. inspection_type",
                },
                {
                    label: "Method",
                    name: "method",
                    type: "single-select",
                    options: [
                        { value: "in", label: "in" },
                        { value: "not_in", label: "not_in" },
                        { value: "starts_with", label: "starts_with" },
                        { value: "is_set", label: "is_set" },
                        { value: "not_current_facility", label: "not_current_facility" },
                    ],
                },
                {
                    label: "Value (comma separated)",
                    name: "value",
                    type: "text",
                    description: "Comma separated values, e.g. avocado,mango. For method 'is_set' use only-empty or only-filled. Not used by 'not_current_facility' (keeps layers whose field does not hold their current facility).",
                },
            ],
            getItemTitle: (item) => {
                if (item?.field) {
                    const source = item.source === "parent_meta" || item.source === "parent_layer" ? `${item.source}.` : "";
                    const value = item.method === "not_current_facility" ? "" : ` ${item.value}`;
                    return `${source}${item.field} ${item.method}${value}`;
                }
                return "New filter";
            },
        },
        {
            label: "Max days in facility",
            description: "Only show layers that have been in the facility for less than this number of days. The user can adjust to their liking",
            name: "max_days_in_facility",
            type: "number"
        },
        {
            label: "Max days in stage",
            description: "Only show layers that have been in the current stage for less than this number of days. The user can adjust to their liking (NOT WORKING YET)",
            name: "max_days_in_stage",
            type: "number"
        },
        {
            label: "Planned work look ahead days",
            description: "Only show layers that have planned work in the future. Fill 0 to show work to be done today, negative values for overdue work. The user can adjust to their liking",
            name: "planned_work_look_ahead_days",
            type: "number"
        },
        {
            label: "Show layers with or without planned work?",
            description: "Show layers with or without planned work in the overview table. Use this to only display unplanned work. Or only planned work without specific date range filled baove.",
            type: "radio",
            name: "show_layers_without_planned_work",
            options: IS_SET_FILTER_OPTIONS
        },
        {
            label: "Show layers with draft reports?",
            description: "Show layers with draft reports in the overview table. Select the type of draft reports to show.",
            name: "show_draft_reports",
            type: "single-select",
            multi: true,
            return_array: true,
            options: config.pdf_reports.map(({ value }) => ({ value: `qc_status_${value}`, label: value }))
        },
        {
            label: "Show layers with sent reports?",
            description: "Show layers with sent reports in the overview table. Select the type of sent reports to show.",
            name: "show_sent_reports",
            type: "single-select",
            multi: true,
            return_array: true,
            options: config.pdf_reports.map(({ value }) => ({ value: `qc_status_${value}`, label: value }))
        },
        {
            label: "Default ordering",
            description: "Default field to order on in the overview table.",
            name: "default_ordering",
            type: "single-select",
            options: sortableOptions
        },
        {
            label: "Pagination limit",
            description: "Maximum items that will be fetched per page. If defined limit buttons won't be shown next to pagination.",
            name: "limit",
            type: "text",
        },
        {
            label: "Default Create Layer type",
            description: "Default layer type to create from this tab. Will default to the layer type being used in the filter configured above.",
            name: "default_create_layer_type",
            type: "single-select",
            options: [{ value: null, label: "Same as filter" }, ...config.layer_types.map((i) => ({ value: i.value, label: i.text }))]
        },
        {
            type: "element",
            label: "Overview table fields",
            description: "What fields to display on this tab in the table.",
            el: <FieldConfigForm
                fields={config_data.layer_overview_fields || []}
                description="What fields to display on this tab in the table."
                name="layer_overview_fields"
                title="Overview table fields"
                useDisplayFilter={["layer_types", "exclude_layer_types", "fruit_types", "exclude_fruit_types"]}
                setArray={(layer_overview_fields) => setConfigData({ ...config_data, layer_overview_fields })} />
        },

        {
            type: "element",
            label: "View strategies",
            description: "Define a list of strategies to select when you click the Eye icon in the layer overview.",
            el: <ViewStrategyConfigForm
                fields={config_data.view_strategies || []}
                description={<span>Define a list of strategies to select when you click the Eye icon in the layer overview.<br></br> Only view strategy SELF is implemented. Is more usefull if we enable AGGREGATE and EXTRAPOLATE again. To be refined.</span>}
                name="view_strategies"
                title="View strategies"
                setArray={(view_strategies) => setConfigData({ ...config_data, view_strategies })} />
        },
        // TODO: we could opt to drop this, for now only use for columns that are not visible or "special filters"
        {
            type: "element",
            label: "Filter form",
            description: "!!Deprecated!! What fields to filter in the overview table.",
            el: <FormConfigForm
                fields={config_data.overview_filter_form || []}
                description="!!Deprecated!! What fields to filter in the overview table."
                name="overview_filter_form"
                title="Filter"
                setArray={(overview_filter_form) => setConfigData({ ...config_data, overview_filter_form })} />
        },
        {
            label: "Call to action title",
            name: "cta_form_title",
            description: "Title of the call to action form and button.",
            type: "text"
        },
        {
            type: "element",
            label: "CTA form",
            description: "Call to action form for overview table filters.",
            el: <FormConfigForm
                fields={config_data.cta_form || []}
                description="Call to action form for overview table filters."
                name="cta_form"
                title="Filter"
                setArray={(cta_form) => setConfigData({ ...config_data, cta_form })} />
        },
        {
            type: "element",
            name: "group_by_fields",
            label: "Group by fields",
            description: "What fields to group by in this tab.",
            el: <OptionsConfigForm
                fields={config_data.group_by_fields || []}
                description="What fields to group by in this tab."
                name="group_by_fields"
                useDisplayFilter={["fruit_types", "exclude_fruit_types"] as any}
                title="Group by fields"
                setArray={(group_by_fields) => setConfigData({ ...config_data, group_by_fields }) } />
        },
        {
            label: "Hide color code",
            name: "hide_color_code",
            description: "Hide color code of this stage in other tabs in LayerStagesColorBadgeField [All_STAGES_STATUS]. You probably want to set this to true for the All tab. ",
            ...yesOrNoRadio
        },
        {
            label: "Status letter",
            name: "status_letter",
            description: "Status letter to display in the overview table.",
            type: "text"
        },
        {
            label: "Show class distribution",
            name: "show_class_distribution",
            description: "Show ripening class distribution in this tab.",
            ...yesOrNoRadio
        },
        // Only allowlisted tenants (Bakker + playwright-test) see the
        // offline-cache toggle on tabs. See allowedTenants.ts.
        ...(showOfflineCacheField ? [{
            label: "Offline cache",
            name: "offline_cache",
            description: "Include the layers from this tab in the offline cache. The /layers/offline-cache/ endpoint returns the union of layers across all tabs marked Yes here, so quality controllers can scan and check pallets from this tab while offline.",
            ...yesOrNoRadio
        }] : []),
    ];

    return <MetaForm withSearch meta={form} setValue={(field, value) => setConfigData({ ...config_data, [field]: value })} object={config_data} config={config} />;
}
